// src/components/PendingQuestionsModal.tsx
//
// Phase 3.7.7 — Pending-questions modal.
//
// Card-based UI for surfacing curator pending_questions to the
// user. Pull-only (the user taps the (?) header icon to open).
//
// Stack order:
//   1. Intro card (briefly explains why we're asking)
//   2. Question cards 1..N (FIFO oldest first)
//   3. Outro card ("Thanks!")
//
// Per-card actions:
//   - free_text:     textarea + Submit
//   - single_select: chips, tap to choose, auto-advance (300ms)
//   - multi_select:  chips with checkboxes, Submit when done
//   - yes_no:        two large buttons, auto-advance (300ms)
//   - custom:        renders as free_text fallback
//   Plus per-card "Skip — ask me later" button.
//
// Footer: "Give me a break" link. Confirmation modal asks
// before flipping the disable flag globally. Open questions
// auto-dismiss server-side and the modal closes.
//
// Transitions: cards slide left/right using MUI Slide. Forward
// is left-out / right-in.

import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Stack,
  Slide,
  Fade,
  LinearProgress,
  Divider,
} from '@mui/material';
import { CloseRounded, CheckCircleRounded } from '@mui/icons-material';
import { useT } from '../i18n/useT';
import {
  PendingQuestion,
  PendingQuestionAnswerFormat,
} from '../hooks/usePendingQuestions';

interface PendingQuestionsModalProps {
  open: boolean;
  onClose: () => void;
  questions: PendingQuestion[];
  onAnswer: (questionId: string, answerText: string) => Promise<void>;
  onSkip: (questionId: string) => void;
  onDisable: () => Promise<void>;
}

type CardSlot =
  | { kind: 'intro' }
  | { kind: 'question'; question: PendingQuestion }
  | { kind: 'outro' };

const ADVANCE_DELAY_MS = 300;

export const PendingQuestionsModal: React.FC<PendingQuestionsModalProps> = ({
  open,
  onClose,
  questions,
  onAnswer,
  onSkip,
  onDisable,
}) => {
  const t = useT();
  // Snapshot the question list when the modal opens so we don't
  // re-shuffle as new questions stream in mid-session. New ones
  // queue up for the next time the user taps the icon.
  //
  // Implementation note: the previous version used a `useRef`
  // updated inside a `useEffect`. That had a bug — the `slots`
  // useMemo only depended on `[open]`, so the very first render
  // after the modal opened computed `slots` from the STALE ref
  // (the effect that refreshes the ref runs AFTER render). The
  // user saw [intro, outro] with no questions on initial open.
  //
  // Switching to state + useLayoutEffect: the snapshot is
  // refreshed synchronously after the open-transition commit
  // but BEFORE the browser paints the stale frame. `slots`
  // recomputes from the fresh snapshot via its dep array.
  const [snapshot, setSnapshot] = useState<PendingQuestion[]>([]);
  const wasOpenRef = useRef(open);
  useLayoutEffect(() => {
    // Only refresh when the modal transitions from closed → open.
    // We deliberately do NOT update on `questions` change while
    // already-open: that would re-shuffle the deck mid-flow.
    if (open && !wasOpenRef.current) {
      setSnapshot(questions);
    }
    wasOpenRef.current = open;
  }, [open, questions]);

  const slots = useMemo<CardSlot[]>(() => {
    if (snapshot.length === 0) {
      return [{ kind: 'intro' }, { kind: 'outro' }];
    }
    return [
      { kind: 'intro' },
      ...snapshot.map((q) => ({ kind: 'question' as const, question: q })),
      { kind: 'outro' },
    ];
  }, [snapshot]);

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('left');
  const [submitting, setSubmitting] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  // Toggle to force a re-mount of the card on advance so that
  // Slide animates cleanly each time.
  const [animKey, setAnimKey] = useState(0);

  // Reset to first card whenever the modal re-opens.
  useEffect(() => {
    if (open) {
      setIndex(0);
      setDirection('left');
      setAnimKey((k) => k + 1);
    }
  }, [open]);

  const total = slots.length;
  const current = slots[index];

  const advance = (delayMs: number = 0) => {
    const next = index + 1;
    const go = () => {
      if (next >= total) {
        // We're past the outro — close the modal.
        onClose();
      } else {
        setDirection('left');
        setIndex(next);
        setAnimKey((k) => k + 1);
      }
    };
    if (delayMs > 0) {
      setTimeout(go, delayMs);
    } else {
      go();
    }
  };

  const handleSkip = (q: PendingQuestion) => {
    onSkip(q.id);
    advance();
  };

  const handleAnswer = async (q: PendingQuestion, text: string) => {
    if (!text || text.trim().length === 0) return;
    setSubmitting(true);
    try {
      await onAnswer(q.id, text.trim());
    } catch (err) {
      // Don't block flow on a transient submission error — log
      // and advance. The question stays open and will return.
      // eslint-disable-next-line no-console
      console.warn('[PendingQuestions] answer submission failed:', err);
    } finally {
      setSubmitting(false);
    }
    advance(ADVANCE_DELAY_MS);
  };

  const handleConfirmDisable = async () => {
    try {
      await onDisable();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PendingQuestions] disable failed:', err);
    }
    setConfirmDisable(false);
    onClose();
  };

  // Compute visible progress only for question cards.
  const questionCount = slots.filter((s) => s.kind === 'question').length;
  const questionIndex =
    current?.kind === 'question'
      ? slots
          .slice(0, index + 1)
          .filter((s) => s.kind === 'question').length
      : 0;
  const showCounter = current?.kind === 'question' && questionCount > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            position: 'relative',
            minHeight: 380,
            overflow: 'hidden',
          },
        }}
      >
        {/* Top bar with close + progress */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
          }}
        >
          <Box sx={{ flex: 1, ml: 2 }}>
            {showCounter && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                {questionIndex} / {questionCount}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label={t("Close")}
            size="small"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <CloseRounded />
          </IconButton>
        </Box>

        {/* Submitting indicator */}
        {submitting && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 3,
            }}
          />
        )}

        <DialogContent
          sx={{
            p: 0,
            pt: 5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 380,
          }}
        >
          {/* Card area */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3,
              py: 2,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Slide
              key={animKey}
              direction={direction === 'left' ? 'left' : 'right'}
              in
              mountOnEnter
              unmountOnExit
              timeout={300}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 2,
                }}
              >
                {current?.kind === 'intro' && (
                  <IntroCard onContinue={() => advance()} />
                )}
                {current?.kind === 'question' && (
                  <QuestionCard
                    question={current.question}
                    onAnswer={(text) => handleAnswer(current.question, text)}
                    onSkip={() => handleSkip(current.question)}
                    submitting={submitting}
                  />
                )}
                {current?.kind === 'outro' && (
                  <OutroCard onClose={onClose} />
                )}
              </Box>
            </Slide>
          </Box>

          {/* Footer — only on intro and question cards */}
          {current?.kind !== 'outro' && (
            <Box>
              <Divider />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  py: 1,
                }}
              >
                <Button
                  size="small"
                  onClick={() => setConfirmDisable(true)}
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                  }}
                >
                  {t('Give me a break')}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation: "Give me a break" */}
      <Dialog
        open={confirmDisable}
        onClose={() => setConfirmDisable(false)}
        maxWidth="xs"
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('Pause questions?')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', mb: 3 }}
          >
            {t("We'll stop asking until you turn this back on in settings.")}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setConfirmDisable(false)}>{t('Cancel')}</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmDisable}
            >
              {t('Pause')}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </>
  );
};

// ──────────────────────────────────────────────────────────────
// Card components
// ──────────────────────────────────────────────────────────────

const IntroCard: React.FC<{ onContinue: () => void }> = ({ onContinue }) => {
  const t = useT();
  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, mb: 2 }}
      >
        {t('Learning about you')}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}
      >
        {t("We're learning what you like — your style, your pace, your interests — to make recommendations that actually fit you.")}
      </Typography>
      <Button
        variant="contained"
        size="large"
        onClick={onContinue}
        sx={{ px: 4, borderRadius: 2 }}
      >
        {t("Let's go")}
      </Button>
    </Box>
  );
};

const OutroCard: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const t = useT();
  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <CheckCircleRounded
        sx={{ fontSize: 56, color: 'success.main', mb: 2 }}
      />
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {t('Thanks! That helps us tune what we recommend for you.')}
      </Typography>
      <Button
        variant="contained"
        size="large"
        onClick={onClose}
        sx={{ px: 4, borderRadius: 2 }}
      >
        {t('Done')}
      </Button>
    </Box>
  );
};

const QuestionCard: React.FC<{
  question: PendingQuestion;
  onAnswer: (text: string) => void;
  onSkip: () => void;
  submitting: boolean;
}> = ({ question, onAnswer, onSkip, submitting }) => {
  const t = useT();
  const format: PendingQuestionAnswerFormat =
    question.answer_format || 'free_text';

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          mb: 3,
          fontWeight: 500,
          lineHeight: 1.4,
        }}
      >
        {question.question}
      </Typography>

      {format === 'yes_no' && (
        <YesNoChoice
          onAnswer={onAnswer}
          disabled={submitting}
        />
      )}

      {format === 'single_select' && (
        <SingleSelectChoice
          options={question.options || []}
          onAnswer={onAnswer}
          disabled={submitting}
        />
      )}

      {format === 'multi_select' && (
        <MultiSelectChoice
          options={question.options || []}
          onAnswer={onAnswer}
          disabled={submitting}
        />
      )}

      {(format === 'free_text' || format === 'custom') && (
        <FreeTextChoice
          suggestions={question.options}
          onAnswer={onAnswer}
          disabled={submitting}
        />
      )}

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          size="small"
          onClick={onSkip}
          disabled={submitting}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
          }}
        >
          {t('Skip — ask me later')}
        </Button>
      </Box>
    </Box>
  );
};

// ─── Choice components ────────────────────────────────────────

const YesNoChoice: React.FC<{
  onAnswer: (text: string) => void;
  disabled: boolean;
}> = ({ onAnswer, disabled }) => {
  const t = useT();
  const [selected, setSelected] = useState<string | null>(null);

  const pick = (val: string) => {
    setSelected(val);
    // Auto-advance with confirmation animation
    setTimeout(() => onAnswer(val), 50);
  };

  return (
    <Stack direction="row" spacing={2} justifyContent="center">
      <Button
        variant={selected === 'Yes' ? 'contained' : 'outlined'}
        size="large"
        disabled={disabled}
        onClick={() => pick('Yes')}
        sx={{ flex: 1, py: 1.5, borderRadius: 2 }}
      >
        {t('Yes')}
      </Button>
      <Button
        variant={selected === 'No' ? 'contained' : 'outlined'}
        size="large"
        disabled={disabled}
        onClick={() => pick('No')}
        sx={{ flex: 1, py: 1.5, borderRadius: 2 }}
      >
        {t('No')}
      </Button>
    </Stack>
  );
};

const SingleSelectChoice: React.FC<{
  options: string[];
  onAnswer: (text: string) => void;
  disabled: boolean;
}> = ({ options, onAnswer, disabled }) => {
  const [selected, setSelected] = useState<string | null>(null);

  const pick = (val: string) => {
    setSelected(val);
    setTimeout(() => onAnswer(val), 50);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      justifyContent="center"
    >
      {options.map((opt) => (
        <Chip
          key={opt}
          label={opt}
          onClick={() => pick(opt)}
          disabled={disabled}
          variant={selected === opt ? 'filled' : 'outlined'}
          color={selected === opt ? 'primary' : 'default'}
          sx={{
            mb: 1,
            px: 1,
            py: 2.5,
            fontSize: '0.95rem',
            cursor: 'pointer',
          }}
        />
      ))}
    </Stack>
  );
};

const MultiSelectChoice: React.FC<{
  options: string[];
  onAnswer: (text: string) => void;
  disabled: boolean;
}> = ({ options, onAnswer, disabled }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (val: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(val)) {
        next.delete(val);
      } else {
        next.add(val);
      }
      return next;
    });
  };

  const submit = () => {
    if (selected.size === 0) return;
    onAnswer(Array.from(selected).join(', '));
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        justifyContent="center"
        sx={{ mb: 2 }}
      >
        {options.map((opt) => (
          <Chip
            key={opt}
            label={opt}
            onClick={() => toggle(opt)}
            disabled={disabled}
            variant={selected.has(opt) ? 'filled' : 'outlined'}
            color={selected.has(opt) ? 'primary' : 'default'}
            sx={{
              mb: 1,
              px: 1,
              py: 2.5,
              fontSize: '0.95rem',
              cursor: 'pointer',
            }}
          />
        ))}
      </Stack>
      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={submit}
          disabled={disabled || selected.size === 0}
          sx={{ px: 4, borderRadius: 2 }}
        >
          {selected.size === 0
            ? 'Pick at least one'
            : `Submit (${selected.size})`}
        </Button>
      </Box>
    </Box>
  );
};

const FreeTextChoice: React.FC<{
  suggestions?: string[];
  onAnswer: (text: string) => void;
  disabled: boolean;
}> = ({ suggestions, onAnswer, disabled }) => {
  const t = useT();
  const [text, setText] = useState('');

  const submit = () => {
    if (text.trim().length === 0) return;
    onAnswer(text.trim());
  };

  return (
    <Box>
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={2}
        maxRows={5}
        placeholder={t("Type your answer…")}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={disabled}
        sx={{ mb: 2 }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
      />
      {suggestions && suggestions.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          {suggestions.map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              onClick={() => setText((cur) => (cur ? `${cur}, ${s}` : s))}
              disabled={disabled}
              variant="outlined"
              sx={{ mb: 0.5 }}
            />
          ))}
        </Stack>
      )}
      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={submit}
          disabled={disabled || text.trim().length === 0}
          sx={{ px: 4, borderRadius: 2 }}
        >
          {t('Submit')}
        </Button>
      </Box>
    </Box>
  );
};
